/**
 * What the five-minute timer should do this time round.
 *
 * # Why this is a function and not four lines in `app.tsx`
 *
 * Because it was four lines in `app.tsx`, and one of them was wrong for months
 * in a way no test could see. The timer lives inside a `useEffect`, so the only
 * way to ask it a question was to render the whole app and wait five minutes;
 * nobody was going to write that, so nobody did, and the bug it hid was the
 * kind that leaves no trace:
 *
 *   - notifications on the desktop are produced only by a replication pass
 *     (`announce` in `core/src/net.rs` is reachable from nowhere else);
 *   - a replication pass came only from `delta.sync`;
 *   - `delta.sync` came only from a stream event.
 *
 * So a stream that stopped delivering stopped *all* notification, permanently,
 * and the backstop could not rescue it because it refreshed the view rather
 * than the replica. The mail still arrived — `populate` in `core/src/net.rs`
 * fills the local copy from whatever the page happens to fetch — so the app
 * looked entirely healthy while having lost the ability to tell anybody
 * anything. The old timer also skipped its whole body unless the window was
 * visible, which on a desktop mail client is the opposite of when a
 * notification matters.
 *
 * The policy is now a value. `app.tsx` decides when to ask and how to carry the
 * answer out; this decides what the answer is, and `backstop.test.ts` can hold
 * it to that.
 */

import type { AuthState } from "./auth";

/** What the app is like at the moment the timer fires. */
export interface Conditions {
  /**
   * The gate's view of the session.
   *
   * The real type rather than a copy of its cases: a union rewritten here would
   * agree with `lib/auth.ts` right up until somebody added a state to one of
   * them, and then disagree silently.
   */
  auth: AuthState;
  /** Whether a session document has been read, so accounts are known. */
  hasSession: boolean;
  /** Whether anybody could be looking at the window. */
  visible: boolean;
}

/**
 * One thing for the caller to do, in the order returned.
 *
 * - `refresh-identity` — ask whether the daemon has come back.
 * - `replicate` — a replication pass, which is also the only thing that can
 *   raise a notification.
 * - `connect` — read the session for the first time.
 * - `refresh-view` — re-read the session, the mailboxes and the list.
 */
export type Action = "refresh-identity" | "replicate" | "connect" | "refresh-view";

export function plan({ auth, hasSession, visible }: Conditions): Action[] {
  // Signed out is not a failure to retry. The gate is already showing the login
  // screen, and asking /jmap on behalf of somebody who is not signed in only
  // feeds the auth-failure counter.
  if (auth === "out") return [];

  // A daemon that was down when the window was opened is worth retrying, and
  // this is the only thing that does it -- the startup effect runs once, and a
  // stream that never opened has nothing to recover from. Nothing else is worth
  // attempting until that answer comes back.
  if (auth === "unreachable") return ["refresh-identity"];

  // Everything else -- `in`, and `unknown` -- carries on below, which is what
  // the timer did before this was a function. `unknown` is the startup state,
  // and a client still in it when the timer fires is one whose first identity
  // check never resolved; the way out is to ask again, which is what `connect`
  // below is. It has no session yet by definition, so it replicates nothing.
  const actions: Action[] = [];

  /*
   * Replication first, and without consulting `visible` at all, because this is
   * the half that is for the person who is *not* looking. It is the only path
   * that reaches `announce`, so it is the only thing that can put a
   * notification on screen -- and a notification is by definition what somebody
   * gets when they are not watching the window. Gating it on visibility made
   * the app able to mention mail only while you could already see it.
   *
   * It needs a session because it needs the account list, and without one there
   * is nothing to name in the request.
   */
  if (hasSession) actions.push("replicate");

  // And this half is for the person who is. Redrawing a list is the one thing
  // here that genuinely needs somebody in front of it.
  if (visible) actions.push(hasSession ? "refresh-view" : "connect");

  return actions;
}
